import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { FaGithub } from "react-icons/fa";
import hdwallet from "../assets/finalhdwallet.png";
import driveimport from "../assets/drive_import.png";
import sndGuard from "../assets/snd-guard.png";
import docLog from "../assets/doc-log.png";

const Projects = () => {
  const projectRefs = useRef([]); // Array of refs for each project
  const headingRef = useRef(null); // Ref for the heading
  const [visibleProjects, setVisibleProjects] = useState([]); // Track visible projects
  const [headingVisible, setHeadingVisible] = useState(false); // Track heading visibility

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const projectIndex = entry.target.dataset.index; // Get the project index
          if (entry.isIntersecting) {
            if (entry.target === headingRef.current) {
              setHeadingVisible(true);
            } else {
              setVisibleProjects((prevVisibleProjects) => [
                ...prevVisibleProjects,
                projectIndex,
              ]);
            }
            observer.unobserve(entry.target); // Stop observing once visible
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      }
    );

    const headingNode = headingRef.current;
    const projectNodes = projectRefs.current;

    if (headingNode) observer.observe(headingNode);

    projectNodes.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      if (headingNode) observer.unobserve(headingNode);
      projectNodes.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  const imageVariant = {
    hidden: { opacity: 0, x: -100 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
  };

  const textVariant = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, delay: 0.2 } },
  };

  const headingVariant = {
    hidden: { opacity: 0, y: -50 },
    visible: { opacity: 1, y: 0, transition: { duration: 1 } },
  };

  return (
    <div className="mx-12 sm:px-16 md:border-b sm:mx-24">
      <motion.h1
        className="myProjects font-extrabold mb-8 sm:text-5xl lg:text-7xl"
        ref={headingRef}
        variants={headingVariant}
        initial="hidden"
        animate={headingVisible ? "visible" : "hidden"}
      >
        Featured Projects
      </motion.h1>
      <div className="">
        {projects.map((project, index) => (
          <div
            key={index}
            className="mb-10 py-5 md:flex justify-between items-center"
            ref={(el) => (projectRefs.current[index] = el)}
            data-index={index}
          >
            {/* FIXED: Added aspect-video and overflow-hidden to the container. 
                This forces all images into a 16:9 ratio regardless of original size.
            */}
            <div className="relative group w-full md:w-1/2 aspect-video overflow-hidden rounded-xl">
              <a
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full h-full"
              >
                <motion.img
                  className="w-full h-full object-cover" 
                  src={project.img}
                  alt="illustration"
                  variants={imageVariant}
                  initial="hidden"
                  animate={visibleProjects.includes(String(index)) ? "visible" : "hidden"}
                />
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white text-lg font-bold">View Project</span>
                </div>
              </a>
            </div>

            <div className="mt-4 md:mt-0 md:ml-8 w-full md:w-1/2">
              <motion.h3
                className="text-xl font-extrabold font-mono leading-8 pt-8 pb-2 sm:text-2xl md:pt-3 lg:text-3xl"
                variants={textVariant}
                initial="hidden"
                animate={visibleProjects.includes(String(index)) ? "visible" : "hidden"}
              >
                {project.title}
              </motion.h3>
              <motion.p
                className="text-gray-400 font-mono text-sm mb-5 lg:text-lg"
                variants={textVariant}
                initial="hidden"
                animate={visibleProjects.includes(String(index)) ? "visible" : "hidden"}
              >
                {project.description}
              </motion.p>
              <a
                href={project.github_link}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <motion.button
                  className="my-2"
                  variants={textVariant}
                  initial="hidden"
                  animate={visibleProjects.includes(String(index)) ? "visible" : "hidden"}
                >
                  <FaGithub size={24} />
                </motion.button>
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const projects = [
  {
    img: sndGuard,
    title: "SNDGuard",
    description: "AI memory security middleware that detects contradictions and memory poisoning in real time, proves trust credentials on Sui, and stores immutable audit trails on Walrus.",
    link: "https://snd-guard-web.vercel.app/",
    github_link: "https://github.com/Bhushan-04/SND-GUARD"
  },
  {
    img: docLog,
    title: "Doc-Log",
    description: "A CLI that writes and maintains codebase documentation for humans and coding agents, powered by the Cursor SDK — no separate model-provider API keys required.",
    link: "https://www.npmjs.com/package/doc-log-cli",
    github_link: "https://github.com/Bhushan-04/doc-log"
  },
  {
    img: driveimport,
    title: "Google drive import",
    description: "A backend-driven integration tool designed to automate file transfers to Google Drive using Service Account authentication for seamless, programmatic server-to-server migration.",
    link: "https://foto-owl-frontend.onrender.com/",
    github_link: "https://github.com/Bhushan-04/Drive_import"
  },
  {
    img: hdwallet,
    title: "Mnemonics Wallet",
    description: "It is a platform where user can generate public-private keypair from single seed phrase for Solana.",
    link: "https://hd-wallet-zeta.vercel.app/",
    github_link: "https://github.com/Bhushan-04/HD-Wallet"
  },
];

export default Projects;